import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useHandleSignInCallback, useLogto, type IdTokenClaims } from '@logto/react';
import { makeClient, type ApiClient, type ApiError } from './api';
import { config } from './config';
import { Link, navigate, usePath } from './router';
import { Spinner } from './components/ui';
import { Rings } from './components/Rings';
import { OverviewPage } from './pages/Overview';
import { ProfilePage } from './pages/Profile';
import { BillingPage } from './pages/Billing';
import { PaymentReturnPage } from './pages/PaymentReturn';
import { DevicesPage } from './pages/Devices';
import { SecurityPage } from './pages/Security';

const RETURN_TO_KEY = 'desktouch:return-to';

interface Session {
  client: ApiClient;
  claims: IdTokenClaims | undefined;
  /** Clears the local session and returns to the sign-in screen. */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside the signed-in shell');
  return session;
}

export function App() {
  if (window.location.pathname === '/callback') return <SignInCallback />;
  return <Shell />;
}

function SignInCallback() {
  const { isLoading, error } = useHandleSignInCallback(() => {
    // Replace, not push: the code-carrying URL must not be reachable with Back.
    const returnTo = safeReturnPath(sessionStorage.getItem(RETURN_TO_KEY));
    sessionStorage.removeItem(RETURN_TO_KEY);
    navigate(returnTo, { replace: true });
    window.location.reload();
  });
  if (error) {
    return (
      <Centered>
        <Rings state="lost" size={96} />
        <h1>Sign-in did not complete</h1>
        <p className="lede">{error.message}</p>
        <a className="button primary" href="/">
          Start again
        </a>
      </Centered>
    );
  }
  return (
    <Centered>
      <Rings state="found" size={96} />
      <p className="lede" role="status">
        {isLoading ? 'Signing you in…' : 'Redirecting…'}
      </p>
    </Centered>
  );
}

/** Only same-origin paths; never a URL someone slipped into storage. */
function safeReturnPath(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/callback')) return '/';
  return value;
}

function Shell() {
  const { isAuthenticated, isLoading, signIn, signOut, getIdTokenClaims, getAccessToken } = useLogto();
  const [claims, setClaims] = useState<IdTokenClaims>();
  const [sessionEnded, setSessionEnded] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    void getIdTokenClaims().then(setClaims);
  }, [isAuthenticated, getIdTokenClaims]);

  // useLogto() hands out a new getAccessToken on every render; pin the client
  // and read the latest function through a ref, or every effect depending on
  // the client refires forever.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const client = useMemo(
    () =>
      makeClient({
        getToken: () => getAccessTokenRef.current(config.apiResource),
        onSessionEnded: (error) => setSessionEnded(error),
      }),
    [],
  );

  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const session = useMemo<Session>(
    () => ({ client, claims, signOut: () => signOutRef.current(window.location.origin) }),
    [client, claims],
  );

  // The SDK flips isLoading around every call, including getAccessToken();
  // only treat it as "loading" before a session exists, or authenticated pages
  // unmount mid-request and refetch in a loop.
  if (isLoading && !isAuthenticated) {
    return (
      <Centered>
        <Spinner label="Loading…" />
      </Centered>
    );
  }

  if (!isAuthenticated) {
    return (
      <SignInScreen
        configured={Boolean(config.logtoAppId)}
        onSignIn={() => {
          sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);
          void signIn(`${window.location.origin}/callback`);
        }}
      />
    );
  }

  if (sessionEnded) {
    return (
      <Centered>
        <h1>Please sign in again</h1>
        <p className="lede">
          {sessionEnded.code === 'account_deleted'
            ? 'This account has been deleted.'
            : sessionEnded.code === 'account_disabled'
              ? 'This account is disabled. Contact support if you think this is a mistake.'
              : 'Your session has ended — for example because you signed out everywhere.'}
        </p>
        <button className="primary" onClick={() => void session.signOut()}>
          Continue
        </button>
      </Centered>
    );
  }

  return (
    <SessionContext.Provider value={session}>
      <Layout />
    </SessionContext.Provider>
  );
}

const NAV = [
  { to: '/', label: 'Overview', icon: 'home' },
  { to: '/billing', label: 'Plan & billing', icon: 'card' },
  { to: '/devices', label: 'Devices', icon: 'devices' },
  { to: '/profile', label: 'Profile', icon: 'user' },
  { to: '/security', label: 'Security & privacy', icon: 'shield' },
] as const;

function Layout() {
  const path = usePath();
  const { claims, signOut } = useSession();
  const name = claims?.name ?? claims?.username ?? claims?.email ?? 'Your account';

  const returnMatch = path.match(/^\/billing\/return\/([0-9a-f-]{36})\/?$/i);
  let page: ReactNode;
  if (returnMatch) page = <PaymentReturnPage paymentId={returnMatch[1]!} />;
  else if (path === '/billing') page = <BillingPage />;
  else if (path === '/devices') page = <DevicesPage />;
  else if (path === '/profile') page = <ProfilePage />;
  else if (path === '/security') page = <SecurityPage />;
  else if (path === '/') page = <OverviewPage />;
  else page = <NotFound />;

  const active = (to: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));

  return (
    <div className="layout">
      <aside className="sidebar">
        <Link to="/" className="brand">
          <Logo />
          <span>
            DeskTouch <em>Account</em>
          </span>
        </Link>
        <nav aria-label="Account sections">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className={active(item.to) ? 'active' : ''}>
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="who">
            {typeof claims?.picture === 'string' && claims.picture ? (
              <img src={claims.picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span className="avatar-fallback">{initials(name)}</span>
            )}
            <span title={name}>{name}</span>
          </div>
          <button type="button" className="ghost" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </aside>
      <main className="content">{page}</main>
    </div>
  );
}

function NotFound() {
  return (
    <div className="empty">
      <h1>Page not found</h1>
      <p className="lede">That address does not exist in your account panel.</p>
      <Link to="/" className="button primary">
        Go to overview
      </Link>
    </div>
  );
}

/** Signed out: the signal rings search behind the card, the way the apps look for a computer. */
function SignInScreen({ configured, onSignIn }: { configured: boolean; onSignIn: () => void }) {
  return (
    <div className="signin">
      <Rings state="searching" fill />
      <div className="signin-card">
        <Logo size={56} />
        <h1>
          Your <span className="signin-brand">DeskTouch</span> account
        </h1>
        <p className="lede">Plan, payments, devices and profile in one place.</p>
        {configured ? (
          <button className="primary large" onClick={onSignIn}>
            Sign in
          </button>
        ) : (
          <div className="notice warning">
            This deployment is missing <code>LOGTO_APP_ID</code>, so sign-in is unavailable.
          </div>
        )}
        <p className="signin-foot">You will be taken to the DeskTouch sign-in page and brought back here.</p>
      </div>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="centered">{children}</div>;
}

function initials(name: string): string {
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

/** The DeskTouch mark: the gradient ribbon "D" with the signal core, as on desktouch.app. */
export function Logo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true" className="logo">
      <defs>
        <linearGradient id="dt-ribbon" x1="18" y1="18" x2="110" y2="104" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#2fd3f5" />
          <stop offset=".3" stopColor="#3a7bf7" />
          <stop offset=".62" stopColor="#8a4cf5" />
          <stop offset=".86" stopColor="#e33fd0" />
          <stop offset="1" stopColor="#f25bb0" />
        </linearGradient>
        <radialGradient id="dt-mint" cx="70" cy="100" r="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3ee3a0" stopOpacity=".95" />
          <stop offset="1" stopColor="#3ee3a0" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="dt-core" x1="46" y1="50" x2="68" y2="72" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#2fd3f5" />
          <stop offset=".55" stopColor="#3a7bf7" />
          <stop offset="1" stopColor="#8a4cf5" />
        </linearGradient>
      </defs>
      <path d="M34 26H64a34 34 0 0 1 0 68H34" stroke="url(#dt-ribbon)" strokeWidth="20" strokeLinecap="round" />
      <path d="M34 26H64a34 34 0 0 1 0 68H34" stroke="url(#dt-mint)" strokeWidth="20" strokeLinecap="round" />
      <g stroke="#fff" strokeLinecap="round">
        <circle cx="56" cy="60" r="21.5" strokeWidth="1.6" opacity=".7" />
        <circle cx="56" cy="60" r="17.5" strokeWidth="2" opacity=".85" />
        <circle cx="56" cy="60" r="13.5" strokeWidth="2.4" />
      </g>
      <circle cx="56" cy="60" r="10" fill="url(#dt-core)" />
    </svg>
  );
}

const ICON_PATHS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  card: 'M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5zM3 10h18M7 15h4',
  devices: 'M3 5.5A1.5 1.5 0 0 1 4.5 4h11A1.5 1.5 0 0 1 17 5.5V13H3zM1 16h18M19 9h2.5a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5H19a.5.5 0 0 1-.5-.5v-9A.5.5 0 0 1 19 9z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  shield: 'M12 3 4.5 6v5.5c0 4.5 3.2 8.2 7.5 9.5 4.3-1.3 7.5-5 7.5-9.5V6zM9 12l2 2 4-4',
};

export function Icon({ name }: { name: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON_PATHS[name] ?? ''} />
    </svg>
  );
}
